import Api from "./api.js";

/**
 * 1Mind — page-level API for the Superhuman AI advisor. Published as `window.OneMind` by app.js,
 * so it is available on every native page. Nothing vendor-side loads until you call embed() or
 * open(); an unused API costs ~2KB.
 *
 * NATIVE COPY of legacy/src2/1mind — see config.js for why it is a copy and what must stay in sync.
 *
 * ── THE THREE WAYS IT CAN RUN ────────────────────────────────────────────────────────────────
 *
 * 1. EMBEDDED — mount inline, right now, in a container you provide. The default.
 *
 *      OneMind.embed({ container: "#onemind-host" });
 *
 * 2. BEHIND THE FORM — start it after a conversion, seeded with what the visitor just typed
 *    (rather than whatever the HubSpot cookie knows). Nothing loads until they submit.
 *
 *      myForm.addEventListener("submit", () => {
 *        OneMind.open({
 *          container: "#onemind-host",
 *          useCase: "behindTheForm",
 *          attributes: { "data-firstname": first.value, "data-email": email.value },
 *        });
 *      });
 *
 *    `attributes` are set verbatim on the launcher script, so they are 1Mind's own names — whatever
 *    the deployment is configured to read. There is no field mapping here on purpose: one used to
 *    translate form names and silently drop anything it did not recognise.
 *
 *    There is deliberately no bind-to-a-form helper: most forms here never fire a native submit
 *    (HubSpot renders in its own iframe, DS3/ChiliPiper run their own handlers), so the caller
 *    owns the trigger.
 *
 * 3. WIDGET — 1Mind's own fixed bottom-right overlay: `useCase: "widget"`. Page-level code should
 *    not use it; the connect bar already owns that corner.
 *
 * ── OPTIONS (embed / open) ───────────────────────────────────────────────────────────────────
 *
 *   container      string|Element   REQUIRED. Where to mount; must already be in the DOM.
 *   useCase        string           embedded (default) | behindTheForm | widget
 *   env            string           sandbox (default, Config.env) | production
 *   attributes     object           extra data-* launcher attributes; empty values are skipped
 *   onReady        function         called with the mount element once the iframe appears
 *   onUnavailable  function         called if it never mounts (~15s)
 *
 *   Returns true if it started, false if it could not (no container, or already running).
 *
 * ── ONE PER PAGE ─────────────────────────────────────────────────────────────────────────────
 *
 * 1Mind mounts into a single fixed element id, so a page can host exactly ONE experience. A
 * second call — including one from the connect bar's AI Advisor panel — is refused with a
 * console warning rather than silently hijacking whatever claimed the page first.
 *
 * The global is OneMind, not 1mind: an identifier cannot start with a digit. `window["1mind"]`
 * is aliased for grep-ability.
 */
export const OneMind = {
  ...Api,

  /** Bring the module up. Same hook every module exposes; no Alpine, so no argument. */
  boot() {
    return this.expose();
  },

  /**
   * Back-compat only. The supported entry point is ThryvApi.oneMind (app/thryv-api); this keeps
   * `window.OneMind` working for legacy page-builder "JavaScript Code" sections written against
   * that exact name. New code must not use it.
   *
   * NOTE the vendor launcher defines `window.onemind` and `window.oneMindConfig` — different
   * objects, distinguished only by casing. Another reason to prefer the namespace.
   */
  expose() {
    window.OneMind = this;
    window["1mind"] = this;

    return this;
  },
};

export default OneMind;
